import {
  CompanyType,
  PathsV1VehicleSearchPostParametersQueryExtend,
  PathsV1VehicleUuidGetParametersQueryExtend,
  RelocationOrderTransportMethod,
} from "@/api-schema-enums";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useVehicleByUuidQuery } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import { useVehicleByVinQuery } from "@/common/api/vehicle/use-vehicle-by-vin.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Combobox } from "@/components/ui/combobox";
import { VehicleInfoCard } from "@/components/vehicle-info-card/vehicle-info-card";
import { vehicleHasOwningBranch } from "@/common/helpers/vehicle-has-owning-branch";
import { ArrowRightIcon, MapPinIcon, StoreIcon, TruckIcon, UserIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import {
  RelocationOrderType,
  useRelocationOrderInitStore,
} from "@/modules/fit/internal/relocation-order/init/_hooks/use-relocation-order-init-store";
import { SelectOrderTypeSkeleton } from "./_components/select-order-type-skeleton";
import { SelectionCard } from "./_components/selection-card";

const NEXT_ROUTE_BY_ORDER_TYPE: Record<RelocationOrderType, string> = {
  [RelocationOrderType.BranchToBranch]: "~/fit/relocation-order/init/select-destination-branch",
  [RelocationOrderType.ExternalAddressToBranch]: "~/fit/relocation-order/init/set-origin-external-address",
  [RelocationOrderType.BranchToExternalAddress]: "~/fit/relocation-order/init/set-destination-external-address",
};

export function InitSelectOrderTypeView() {
  const { t } = useTranslation(["fit"]);
  const [_, navigate] = useLocation();
  const search = useSearch();

  const searchParams = new URLSearchParams(search);
  const vehicleUuidParam = searchParams.get("vehicleUuid");
  const vinParam = searchParams.get("vin");
  const normalizedVinParam = vinParam ? vinParam.trim().toUpperCase() || null : null;

  const transportMethod = useRelocationOrderInitStore((state) => state.transportMethod);
  const setTransportMethod = useRelocationOrderInitStore((state) => state.setTransportMethod);
  const setTransportCompanyUuid = useRelocationOrderInitStore((state) => state.setTransportCompanyUuid);
  const relocationOrderType = useRelocationOrderInitStore((state) => state.relocationOrderType);
  const setRelocationOrderType = useRelocationOrderInitStore((state) => state.setRelocationOrderType);
  const setVehicle = useRelocationOrderInitStore((state) => state.setVehicle);

  const {
    data: vehicleByUuid,
    isLoading: vehicleByUuidIsLoading,
    isError: vehicleByUuidIsError,
  } = useVehicleByUuidQuery(vehicleUuidParam ?? "", {
    parameters: {
      extend: [
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch,
      ],
    },
  });

  const {
    data: vehicleByVin,
    isLoading: vehicleByVinIsLoading,
    isError: vehicleByVinIsError,
  } = useVehicleByVinQuery(!vehicleUuidParam ? (normalizedVinParam ?? "") : "", {
    parameters: {
      extend: [
        PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration,
        PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily,
        PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch,
        PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch,
      ],
    },
  });

  const queryVehicle = vehicleByUuid ?? vehicleByVin;
  const hasVehicleQueryParam = !!(vehicleUuidParam || normalizedVinParam);
  const vehicleQueryIsLoading = vehicleByUuidIsLoading || vehicleByVinIsLoading;
  const vehicleIsMissingOwningBranch = !!queryVehicle && !vehicleHasOwningBranch(queryVehicle);
  const vehicleQueryFailed =
    hasVehicleQueryParam &&
    !vehicleQueryIsLoading &&
    (vehicleByUuidIsError ||
      vehicleByVinIsError ||
      queryVehicle === null ||
      queryVehicle === undefined ||
      vehicleIsMissingOwningBranch);

  useEffect(() => {
    if (vehicleQueryFailed) {
      navigate("~/fit/relocation-order/init/select-vehicle", { replace: true });
    }
  }, [vehicleQueryFailed, navigate]);

  useEffect(() => {
    if (queryVehicle && !vehicleIsMissingOwningBranch) {
      setVehicle(queryVehicle);
    }
  }, [queryVehicle, setVehicle, vehicleIsMissingOwningBranch]);

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState<string | null>(null);

  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination();

  const { data: companies, isLoading: companiesAreLoading } = useCompanySearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body: {
        type: [CompanyType.Transport],
      },
    },
  });

  const transportCompanies = companies?.data ?? [];
  const totalTransportCompanies = companies?.meta?.total ?? 0;
  const isSingleTransportCompany = totalTransportCompanies === 1;
  const isTransportCompanyTransportMethod = transportMethod === RelocationOrderTransportMethod.TransportCompany;

  const isSubmitDisabled = isTransportCompanyTransportMethod && !isSingleTransportCompany && !selectedCompanyUuid;

  const paginationState = createPaginationState(
    totalTransportCompanies,
    transportCompanies.length,
    companiesAreLoading,
  );

  const transportMethodOptions = useMemo(
    () => [
      {
        value: RelocationOrderTransportMethod.TransportCompany,
        label: t("fit:TransportCompany"),
        icon: <TruckIcon className="h-6 w-6" />,
      },
      {
        value: RelocationOrderTransportMethod.OwnAxle,
        label: t("fit:TransportMethodOwnAxle"),
        icon: <UserIcon className="h-6 w-6" />,
      },
    ],
    [t],
  );

  const relocationOrderTypeOptions = useMemo(
    () => [
      {
        value: RelocationOrderType.BranchToBranch,
        label: t("fit:BranchToBranch"),
        icon: (
          <div className="flex items-center gap-2">
            <StoreIcon className="h-6 w-6" />
            <ArrowRightIcon className="h-4 w-4" />
            <StoreIcon className="h-6 w-6" />
          </div>
        ),
      },
      {
        value: RelocationOrderType.BranchToExternalAddress,
        label: t("fit:BranchToExternal"),
        icon: (
          <div className="flex items-center gap-2">
            <StoreIcon className="h-6 w-6" />
            <ArrowRightIcon className="h-4 w-4" />
            <MapPinIcon className="h-6 w-6" />
          </div>
        ),
      },
      {
        value: RelocationOrderType.ExternalAddressToBranch,
        label: t("fit:ExternalToBranch"),
        icon: (
          <div className="flex items-center gap-2">
            <MapPinIcon className="h-6 w-6" />
            <ArrowRightIcon className="h-4 w-4" />
            <StoreIcon className="h-6 w-6" />
          </div>
        ),
      },
    ],
    [t],
  );

  const handleNext = () => {
    if (isTransportCompanyTransportMethod) {
      const companyUuid = isSingleTransportCompany ? transportCompanies[0].uuid : selectedCompanyUuid;
      setTransportCompanyUuid(companyUuid);
    }

    navigate(NEXT_ROUTE_BY_ORDER_TYPE[relocationOrderType]);
  };

  if (companiesAreLoading || vehicleQueryIsLoading) {
    return <SelectOrderTypeSkeleton />;
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Default}
      title={t("fit:SelectOrderType")}
      subTitle={t("fit:SelectOrderTypeDescription")}
    >
      {hasVehicleQueryParam && queryVehicle && <VehicleInfoCard vehicle={queryVehicle} />}
      <div className="flex flex-col gap-2">
        <h2>{t("fit:TransportMethod")}</h2>

        <div className="grid grid-cols-2 gap-4">
          {transportMethodOptions.map((option) => (
            <SelectionCard
              key={option.value}
              label={option.label}
              icon={option.icon}
              isSelected={transportMethod === option.value}
              onClick={() => setTransportMethod(option.value)}
            />
          ))}
        </div>

        <Collapsible
          open={isTransportCompanyTransportMethod && !isSingleTransportCompany && transportCompanies.length > 1}
        >
          <CollapsibleContent>
            <div className="flex flex-col gap-2">
              <h2>{t("fit:TransportCompany")}</h2>

              <Combobox
                options={transportCompanies.map((company) => ({
                  value: company.uuid,
                  label: company.name,
                }))}
                value={selectedCompanyUuid ?? ""}
                onChange={(value) => setSelectedCompanyUuid(value || null)}
                placeholder={t("fit:SelectTransportCompany")}
                searchPlaceholder={t("fit:SearchTransportCompany")}
                emptyText={t("fit:NoTransportCompanyFound")}
                shouldFilter={false}
                isLoading={paginationState.isLoading}
                hasMore={paginationState.hasMore}
                remainingCount={paginationState.remainingCount}
                isLoadingMore={paginationState.isLoadingMore}
                onSearchChange={paginationState.onSearchChange}
                onLoadMore={paginationState.onLoadMore}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="flex flex-col gap-2">
        <h2>{t("fit:RelocationOrderTypeLabel")}</h2>

        <div className="grid grid-cols-3 gap-4">
          {relocationOrderTypeOptions.map((option) => (
            <SelectionCard
              key={option.value}
              label={option.label}
              icon={option.icon}
              isSelected={relocationOrderType === option.value}
              onClick={() => setRelocationOrderType(option.value)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-row justify-end">
        <Button onClick={handleNext} disabled={isSubmitDisabled}>
          {t("fit:Next")}
        </Button>
      </div>
    </CenteredLayout>
  );
}
